import { BaseCommandHandler, CommandResult, PropertyChange } from '../BaseCommandHandler';
import { Component, ComponentType } from '@/types';

export class ModalCommandHandler extends BaseCommandHandler {

  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Title — "title My Modal" or "set title to My Modal"
    this.commandPatterns.set(
      /(?:set\s+)?title\s+(?:to\s+)?(.+)/i,
      (match) => ({ properties: { title: match[1].trim() } })
    );

    // Trigger variant — MUST be registered BEFORE generic trigger
    this.commandPatterns.set(
      /trigger\s+variant\s+(primary|secondary|outline|ghost)/i,
      (match) => ({ properties: { triggerVariant: match[1].toLowerCase() } })
    );

    // Trigger text — "trigger Click Here" or "set trigger to Click Here"
    this.commandPatterns.set(
      /(?:set\s+)?trigger\s+(?:text\s+)?(?:to\s+)?(.+)/i,
      (match) => {
        const text = match[1].trim();
        // Don't match if this is a "trigger variant" command
        if (/^variant\s+/i.test(text)) return null;
        return { properties: { triggerText: text } };
      }
    );

    // Size
    this.commandPatterns.set(
      /(?:set\s+)?size\s+(?:to\s+)?(sm|small|md|medium|lg|large|full|fullwidth)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          sm: 'sm', small: 'sm', md: 'md', medium: 'md',
          lg: 'lg', large: 'lg', full: 'full', fullwidth: 'full',
        };
        return { properties: { size: sizeMap[match[1].toLowerCase()] || match[1].toLowerCase() } };
      }
    );

    // Backdrop on/off
    this.commandPatterns.set(
      /backdrop\s+(on|off|true|false|yes|no)/i,
      (match) => ({
        properties: { showBackdrop: ['on', 'true', 'yes'].includes(match[1].toLowerCase()) }
      })
    );

    // Close button on/off
    this.commandPatterns.set(
      /close\s+button\s+(on|off|true|false|yes|no)/i,
      (match) => ({
        properties: { showCloseButton: ['on', 'true', 'yes'].includes(match[1].toLowerCase()) }
      })
    );

    // Animation type
    this.commandPatterns.set(
      /animation\s+(fade|slide|none)/i,
      (match) => ({ properties: { animationType: match[1].toLowerCase() } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.MODAL && component.type !== ('modal' as ComponentType)) {
      return false;
    }
    const modalKeywords = ['title', 'trigger', 'size', 'backdrop', 'close button', 'animation'];
    const cmd = command.toLowerCase();
    return modalKeywords.some(kw => cmd.includes(kw));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    if (!this.canHandle(command, component)) {
      return {
        success: false,
        error: 'This handler only works with Modal components',
        suggestions: await this.getSuggestions(component),
      };
    }

    const changes = this.matchPatterns(command, component);

    if (changes && Object.keys(changes.properties || {}).length > 0) {
      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
      };
    }

    return {
      success: false,
      error: 'Could not understand the modal command',
      suggestions: await this.getSuggestions(component),
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      'title My Modal Title',
      'trigger Click Here',
      'trigger variant outline',
      'size lg',
      'backdrop off',
      'close button off',
      'animation slide',
    ];
  }

  async getSuggestions(_component: Component): Promise<string[]> {
    return [
      'title ', 'trigger ', 'trigger variant ', 'size ',
      'backdrop on', 'backdrop off', 'close button on', 'close button off',
      'animation fade', 'animation slide', 'animation none',
    ];
  }
}

export const modalCommandHandler = new ModalCommandHandler();
